import { useState } from "react";
import { Check, EyeOff, Loader2, Pencil, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI, slugifySport } from "../../lib/utils";
import { DeactivateSportDialog } from "../sports/DeactivateSportDialog";
import { DeleteSportDialog } from "../sports/DeleteSportDialog";
import { sportConflictMessage } from "../sports/sportConflict";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";

/**
 * Gli sport che il coach ha creato (D1, D5 di docs/sport-config).
 *
 * Restano suoi finché l'admin non li rende globali. Li rinomina; li disattiva
 * vedendo prima chi li usa; li riattiva. Se nessuno li usa (a parte il coach
 * che li ha creati), il server dichiara `deletable`: allora si può anche
 * cambiarne l'identificativo, che è il modo di correggere un typo, o cancellarli.
 *
 * Props: sports (`created` di `/sport-config`), onChanged() (async).
 */
export function CreatedSportsCard({ sports = [], onChanged }) {
	const { t } = useLanguage();
	const [editingId, setEditingId] = useState(null);
	const [draft, setDraft] = useState("");
	const [draftValue, setDraftValue] = useState("");
	const [busyId, setBusyId] = useState(null);
	const [deactivating, setDeactivating] = useState(null);
	const [deleting, setDeleting] = useState(null);

	if (!sports.length) return null;

	const update = async (sport, body, successKey) => {
		setBusyId(sport.sport_id);
		try {
			const updated = await fetchAPI(`/sports/${encodeURIComponent(sport.sport_id)}`, {
				method: "PUT",
				body: JSON.stringify(body),
			});
			toast.success(t(successKey).replace("{label}", updated?.label || sport.label));
			setEditingId(null);
			await onChanged?.();
		} catch (error) {
			toast.error(sportConflictMessage(error, t, "coach"));
		} finally {
			setBusyId(null);
		}
	};

	const remove = async (sport) => {
		setBusyId(sport.sport_id);
		try {
			await fetchAPI(`/sports/${encodeURIComponent(sport.sport_id)}`, { method: "DELETE" });
			toast.success(t("scDeleted").replace("{label}", sport.label || sport.value));
			await onChanged?.();
		} catch (error) {
			toast.error(sportConflictMessage(error, t, "coach"));
		} finally {
			setBusyId(null);
		}
	};

	const startRename = (sport) => {
		setEditingId(sport.sport_id);
		setDraft(sport.label || "");
		setDraftValue(sport.value || "");
	};

	// L'identificativo si cambia solo se lo sport non è mai stato usato: il
	// server lo rifiuta comunque, qui si evita di mandarlo.
	const saveRename = (sport) => {
		const body = { label: draft.trim() };
		if (sport.deletable) {
			const value = slugifySport(draftValue);
			if (value !== sport.value) body.value = value;
		}
		update(sport, body, "scSportRenamed");
	};

	const canSave = (sport) =>
		!!draft.trim() && (!sport.deletable || !!slugifySport(draftValue));

	return (
		<Card className="bg-card border-border rounded-sm">
			<CardHeader className="pb-2">
				<CardTitle className="font-heading text-lg">{t("scCreatedTitle")}</CardTitle>
				<p className="text-sm text-muted-foreground">{t("scCreatedHint")}</p>
			</CardHeader>
			<CardContent className="space-y-2">
				{sports.map((sport) => {
					const busy = busyId === sport.sport_id;
					const usage = sport.usage || {};
					const editing = editingId === sport.sport_id;
					return (
						<div
							key={sport.sport_id}
							className="flex flex-col gap-2 rounded-sm border border-border bg-secondary/30 p-3 sm:flex-row sm:items-center sm:justify-between"
							data-testid={`created-sport-${sport.value}`}
						>
							<div className="min-w-0 flex-1 space-y-1">
								{editing ? (
									<div className="space-y-1.5">
										<div className="flex items-center gap-1.5">
											<Input
												value={draft}
												maxLength={60}
												autoFocus
												aria-label={t("sdfLabel")}
												onChange={(event) => setDraft(event.target.value)}
												onKeyDown={(event) => {
													if (event.key === "Enter" && canSave(sport)) saveRename(sport);
													if (event.key === "Escape") setEditingId(null);
												}}
												className="h-8 bg-secondary border-transparent rounded-sm"
											/>
											<Button
												size="icon"
												variant="ghost"
												className="h-8 w-8 rounded-sm"
												aria-label={t("save")}
												disabled={busy || !canSave(sport)}
												onClick={() => saveRename(sport)}
											>
												{busy ? (
													<Loader2 className="w-3.5 h-3.5 animate-spin" />
												) : (
													<Check className="w-3.5 h-3.5" />
												)}
											</Button>
											<Button
												size="icon"
												variant="ghost"
												className="h-8 w-8 rounded-sm"
												aria-label={t("cancel")}
												disabled={busy}
												onClick={() => setEditingId(null)}
											>
												<X className="w-3.5 h-3.5" />
											</Button>
										</div>
										{sport.deletable && (
											<div className="space-y-0.5">
												<Input
													value={draftValue}
													maxLength={60}
													aria-label={t("scIdentifier")}
													onChange={(event) => setDraftValue(event.target.value)}
													className="h-8 bg-secondary border-transparent rounded-sm font-mono text-xs"
												/>
												<p className="text-xs text-muted-foreground">
													{t("scIdentifierHint").replace(
														"{value}",
														slugifySport(draftValue) || "—",
													)}
												</p>
											</div>
										)}
									</div>
								) : (
									<div className="flex flex-wrap items-center gap-2">
										<p className="font-medium truncate">{sport.label}</p>
										<Badge
											className={`border-0 ${
												sport.is_active
													? "bg-green-500/20 text-green-400"
													: "bg-red-500/20 text-red-400"
											}`}
										>
											{sport.is_active ? t("scSportActive") : t("scSportInactive")}
										</Badge>
										{!sport.followed && (
											<Badge className="bg-amber-500/20 text-amber-300 border-0">
												{t("scNotFollowed")}
											</Badge>
										)}
									</div>
								)}
								<p className="text-xs text-muted-foreground">
									{t("scCreatedUsage")
										.replace("{athletes}", usage.athletes ?? 0)
										.replace("{models}", usage.models ?? 0)}
								</p>
							</div>
							{!editing && (
								<div className="flex flex-wrap items-center gap-1.5 shrink-0">
									<Button
										variant="ghost"
										size="icon"
										className="h-8 w-8 rounded-sm"
										title={t("scRename")}
										aria-label={t("scRename")}
										disabled={busy}
										onClick={() => startRename(sport)}
									>
										<Pencil className="w-3.5 h-3.5" />
									</Button>
									{sport.deletable && (
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 rounded-sm hover:text-red-500"
											title={t("scDelete")}
											aria-label={t("scDelete")}
											disabled={busy}
											onClick={() => setDeleting(sport)}
										>
											<Trash2 className="w-3.5 h-3.5" />
										</Button>
									)}
									{sport.is_active ? (
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 rounded-sm hover:text-red-500"
											title={t("sdDeactivate")}
											aria-label={t("sdDeactivate")}
											disabled={busy}
											onClick={() => setDeactivating(sport)}
										>
											<EyeOff className="w-3.5 h-3.5" />
										</Button>
									) : (
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 rounded-sm hover:text-green-400"
											title={t("sdReactivate")}
											aria-label={t("sdReactivate")}
											disabled={busy}
											onClick={() => update(sport, { is_active: true }, "sdReactivated")}
										>
											<RotateCcw className="w-3.5 h-3.5" />
										</Button>
									)}
								</div>
							)}
						</div>
					);
				})}
			</CardContent>
			<DeactivateSportDialog
				sport={deactivating}
				open={!!deactivating}
				onOpenChange={(open) => {
					if (!open) setDeactivating(null);
				}}
				usagePath={
					deactivating
						? `/sports/${encodeURIComponent(deactivating.sport_id)}/usage`
						: null
				}
				onConfirm={() => update(deactivating, { is_active: false }, "sdDeactivated")}
			/>
			<DeleteSportDialog
				sport={deleting}
				open={!!deleting}
				onOpenChange={(open) => {
					if (!open) setDeleting(null);
				}}
				onConfirm={() => remove(deleting)}
			/>
		</Card>
	);
}
